import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCircle, AlertCircle, Info, XCircle, RefreshCw, ArrowLeft, Zap, ZapOff
} from 'react-feather';
import { useNotifications } from '../contexts/NotificationContext';

const TestNotificationsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('Ceci est un message de test');
  const [notificationType, setNotificationType] = useState('info');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { isConnected, unreadCount } = useNotifications();

  const sendTestNotification = async (type, customMessage = null) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: customMessage || message,
          type
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de l\'envoi de la notification');
      }
      
      setResult({
        success: true,
        message: 'Notification de test envoyée avec succès!',
        data: data.notification
      });
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'envoi de la notification');
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'warning':
        return <AlertCircle className="text-yellow-500" size={20} />;
      case 'error':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Info className="text-blue-500" size={20} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="mr-2" size={16} />
            Retour
          </button>
          
          <div className="md:flex md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Test des notifications</h1>
              <p className="mt-1 text-sm text-gray-500">
                Envoyez des notifications de test pour vérifier le bon fonctionnement du système.
              </p>
            </div>
            
            <div className="mt-4 flex items-center md:mt-0">
              <div className={`flex items-center text-sm ${isConnected ? 'text-green-600' : 'text-yellow-600'}`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                {isConnected ? 'Connecté en temps réel' : 'Hors ligne'}
              </div>
              <div className="ml-4 flex items-center text-sm text-gray-500">
                <Bell className="mr-1.5 h-4 w-4 text-gray-400" />
                {unreadCount} non lue{unreadCount !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Envoyer une notification de test
            </h3>
            
            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Entrez votre message de test"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Type de notification
                </label>
                <select
                  id="type"
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="info">Information</option>
                  <option value="success">Succès</option>
                  <option value="warning">Avertissement</option>
                  <option value="error">Erreur</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => sendTestNotification(notificationType, message)}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Zap className="-ml-1 mr-2 h-4 w-4" />
                    Envoyer une notification de test
                  </>
                )}
              </button>
            </div>

            <div className="mt-8">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Actions rapides</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  type="button"
                  onClick={() => sendTestNotification('info', 'Ceci est une notification d\'information')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <Info className="mr-2 h-4 w-4 text-blue-500" />
                  Info
                </button>
                
                <button
                  type="button"
                  onClick={() => sendTestNotification('success', 'Opération réussie avec succès!')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                  Succès
                </button>
                
                <button
                  type="button"
                  onClick={() => sendTestNotification('warning', 'Attention: action requise')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                >
                  <AlertCircle className="mr-2 h-4 w-4 text-yellow-500" />
                  Avertissement
                </button>
                
                <button
                  type="button"
                  onClick={() => sendTestNotification('error', 'Une erreur critique est survenue')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  <XCircle className="mr-2 h-4 w-4 text-red-500" />
                  Erreur
                </button>
              </div>
            </div>
          </div>
          
          {/* Result/Error Display */}
          {(result || error) && (
            <div className={`border-t border-gray-200 px-4 py-5 sm:p-6 ${error ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {error ? (
                    <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${error ? 'text-red-800' : 'text-green-800'}`}>
                    {error ? 'Erreur' : 'Succès'}
                  </h3>
                  <div className={`mt-2 text-sm ${error ? 'text-red-700' : 'text-green-700'}`}>
                    <p>{error || result?.message}</p>
                  </div>
                  {result?.data && (
                    <div className="mt-4">
                      <div className="bg-white p-3 rounded-md border border-gray-200 overflow-x-auto">
                        <pre className="text-xs text-gray-700">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Informations de débogage
            </h3>
            <div className="mt-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Statut de la connexion</h4>
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-2 bg-green-500"></div>
                  <span className="text-sm font-medium">
                    Connecté (API)
                  </span>
                </div>
                
                <h4 className="text-sm font-medium text-gray-500 mt-4 mb-2">Notifications non lues</h4>
                <p className="text-sm">{unreadCount} notification{unreadCount !== 1 ? 's' : ''} non lue{unreadCount !== 1 ? 's' : ''}</p>
                
                <h4 className="text-sm font-medium text-gray-500 mt-4 mb-2">Dernier événement</h4>
                <div className="text-sm bg-white p-3 rounded border border-gray-200">
                  {result?.data ? (
                    <pre className="text-xs">
                      {JSON.stringify({
                        id: result.data.id,
                        type: result.data.type,
                        title: result.data.title,
                        message: result.data.message,
                        isRead: result.data.is_read,
                        createdAt: result.data.created_at
                      }, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-gray-500 italic">Aucun événement récent</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestNotificationsPage;
